import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { Loader2, X, Image as ImageIcon, ChevronLeft, ChevronRight, Check } from 'lucide-react';

import type { ManifestData } from '../utils/manifestUtils';
import { findSegmentForPOI } from '../utils/manifestUtils';
import {
  worldToGeodetic,
  projectToScreen,
  type WorldPos,
  type ScreenPos,
  markerColor,
  markerLabel,
} from '../utils/measureUtils';
import {
  fetchImageIndexStore,
  type ImageIndexStore,
} from '../utils/imageIndexUtils';
import {
  selectSeedCandidates,
  expandFromSeedCandidate,
  type RankedImageCandidate,
} from '../utils/imageRankingUtils';
import { customerApi } from '../services/customerApi';

interface SourceImagesProps {
  viewerRef: React.MutableRefObject<any>;
  manifest: ManifestData | null;
  sceneId?: string;
  isActive: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLButtonElement>;
}

type SourceImageStep = 'idle' | 'capturing' | 'done';
type PendingPointerIntent = { kind: 'create-point' } | null;

function getFallbackSegmentFromViewer(viewer: any, wp: WorldPos): number | null {
  const lazySegments = viewer?.lazyLoadSegments;
  if (!lazySegments || typeof lazySegments.values !== 'function') return null;

  // 1) Best case: clicked point lies inside a segment view box
  for (const segment of lazySegments.values()) {
    if (segment?.viewBoundingBox?.containsPoint?.(wp)) {
      return segment.index ?? null;
    }
  }

  // 2) Next best: camera lies inside a segment view box
  const cameraPos = viewer?.camera?.position;
  if (cameraPos) {
    for (const segment of lazySegments.values()) {
      if (segment?.viewBoundingBox?.containsPoint?.(cameraPos)) {
        return segment.index ?? null;
      }
    }
  }

  // 3) Last fallback: use currently active visible mesh -> map back to segment index
  const activeMeshId = viewer?.activeSplatMeshId;
  if (activeMeshId !== null && activeMeshId !== undefined) {
    for (const segment of lazySegments.values()) {
      if (segment?.sceneIndex === activeMeshId) {
        return segment.index ?? null;
      }
    }
  }

  return null;
}

const getImageIndexUrlForScene = (sceneId?: string): string | null => {
  if (!sceneId) return null;
  switch (sceneId.toLowerCase()) {
    case 'terrebonne': return '/terrebonne_compact_index.json';
    default: return null;
  }
};

// ─── LightboxViewer ───────────────────────────────────────────────────────────

interface LightboxViewerProps {
  img:     RankedImageCandidate | undefined;
  url:     string | undefined;
  index:   number;
  total:   number;
  onClose: () => void;
  onPrev:  () => void;
  onNext:  () => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

const LightboxViewer: React.FC<LightboxViewerProps> = ({
  img, url, index, total, onClose, onPrev, onNext,
}) => {
  const { t } = useI18n();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan]   = useState({ x: 0, y: 0 });
  const dragging        = useRef(false);
  const lastPos         = useRef({ x: 0, y: 0 });
  const stageRef        = useRef<HTMLDivElement>(null);
  
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [index]);

  const clampPan = (z: number, px: number, py: number) => {
    const max = ((z - 1) / 2) * 100;
    return { x: Math.max(-max, Math.min(max, px)), y: Math.max(-max, Math.min(max, py)) };
  };

  useEffect(() => {
  const el = stageRef.current;
  if (!el) return;
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    setZoom(z => {
      const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z - e.deltaY * 0.001 * z));
      if (next === MIN_ZOOM) setPan({ x: 0, y: 0 });
      return next;
    });
  };
  el.addEventListener('wheel', onWheel, { passive: false });
  return () => el.removeEventListener('wheel', onWheel);
}, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setPan(p => clampPan(zoom, p.x + dx * 0.15, p.y + dy * 0.15));
  };

  return (
    <div
      className="fixed inset-0 z-[1010] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(4px)' }}
      onClick={zoom === 1 ? onClose : undefined}
    >
      <button onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ position: 'absolute', top: 20, right: 20, zIndex: 2, width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <X size={16} />
      </button>

      {/* Zoom controls */}
      <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 2, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.5)', borderRadius: 10, padding: '4px 10px', border: '1px solid rgba(255,255,255,0.12)' }}>
        <button onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(MIN_ZOOM, +(z - 0.5).toFixed(1))); }}
          style={{ color: 'white', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>−</button>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', minWidth: 36, textAlign: 'center', fontFamily: 'monospace' }}>{zoom.toFixed(1)}×</span>
        <button onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(MAX_ZOOM, +(z + 0.5).toFixed(1))); }}
          style={{ color: 'white', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>+</button>
        {zoom > 1 && <button onClick={(e) => { e.stopPropagation(); setZoom(1); setPan({ x: 0, y: 0 }); }}
          style={{ color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, padding: '0 4px' }}>{t.sourceImages.zoomReset}</button>}
      </div>

      {index > 0 && (
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }}
          style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChevronLeft size={20} />
        </button>
      )}
      {index < total - 1 && (
        <button onClick={(e) => { e.stopPropagation(); onNext(); }}
          style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChevronRight size={20} />
        </button>
      )}

      {/* Image stage — ref for non-passive wheel listener */}
      <div
        ref={stageRef}
        style={{ overflow: 'hidden', width: '85vw', height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={() => { dragging.current = false; }}
        onMouseLeave={() => { dragging.current = false; }}
        onClick={(e) => e.stopPropagation()}
      >
        {url ? (
          <img src={url} alt={img?.name} draggable={false} style={{
            maxWidth: '100%', maxHeight: '100%', borderRadius: zoom === 1 ? 12 : 4,
            boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
            transform: `scale(${zoom}) translate(${pan.x}%, ${pan.y}%)`,
            transformOrigin: 'center center',
            transition: dragging.current ? 'none' : 'transform 0.15s ease',
            cursor: zoom > 1 ? (dragging.current ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
          }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)' }}>
            <ImageIcon size={48} />
          </div>
        )}
      </div>

      {img && (
        <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
          #{index + 1} — {img.name} · {img.distanceMeters.toFixed(1)} m
          <span style={{ marginLeft: 12, color: 'rgba(255,255,255,0.25)' }}>
            {zoom > 1 ? t.sourceImages.scrollZoomDragPan : t.sourceImages.scrollToZoom}
          </span>
        </div>
      )}
    </div>
  );
};

// ─── Step 1: Seed Grid Overlay ────────────────────────────────────────────────

interface SeedGridProps {
  seeds:       RankedImageCandidate[];
  imageUrls:   Record<string, string>;
  urlsLoading: boolean;
  onPick:      (seed: RankedImageCandidate) => void;
  onReset:     () => void;
  onClose:     () => void;
}

const SEEDS_PER_PAGE = 5;

const SeedGrid: React.FC<SeedGridProps> = ({
  seeds, imageUrls, urlsLoading, onPick, onReset, onClose,
}) => {
  const { t } = useI18n();
  const [hovered, setHovered] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const totalPages   = Math.ceil(seeds.length / SEEDS_PER_PAGE);
  const visibleSeeds = seeds.slice(page * SEEDS_PER_PAGE, (page + 1) * SEEDS_PER_PAGE);
  const hasMore = page < totalPages - 1;
  const hasPrev = page > 0;

  return (
    <div
      className="fixed z-[1005] pointer-events-auto"
      style={{
        bottom: 0, left: 0, right: 0,
        background: 'rgba(0,0,0,0.82)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderTop: '1px solid rgba(255,255,255,0.10)',
        padding: '14px 20px 16px',
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>
            {t.sourceImages.seedQuestion}
          </span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
            {t.sourceImages.seedHint}
          </span>
          {totalPages > 1 && (
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
              {page + 1} / {totalPages}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onReset}
            style={{ padding: '5px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.65)', fontSize: 11, cursor: 'pointer' }}>
            {t.sourceImages.pickDifferentPoint}
          </button>
          <button onClick={onClose}
            style={{ padding: '5px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)', color: 'white', fontSize: 11, cursor: 'pointer' }}>
            {t.sourceImages.close}
          </button>
        </div>
      </div>

      {/* Thumbnail tray */}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>

        {/* Prev — only shown when on page > 0 */}
        {hasPrev ? (
          <button
            onClick={() => setPage(p => p - 1)}
            style={{
              flexShrink: 0, width: 48, height: 165,
              borderRadius: 10,
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.25)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
              fontSize: 10, fontWeight: 600,
            }}
          >
            <ChevronLeft size={18} />
            <span>{t.sourceImages.prev}</span>
          </button>
        ) : (
          /* placeholder so grid doesn't shift on page 0 */
          <div style={{ flexShrink: 0, width: 48 }} />
        )}

        {/* 5 thumbnails — equal width */}
        <div style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: `repeat(${SEEDS_PER_PAGE}, 1fr)`, gap: 10 }}>
          {visibleSeeds.map((seed, i) => {
            const url = imageUrls[seed.name];
            const seedKey = String(seed.id);
            const isHovered = hovered === seedKey;
            const globalIndex = page * SEEDS_PER_PAGE + i;
            return (
              <div
                key={seed.id}
                onClick={() => onPick(seed)}
                onMouseEnter={() => setHovered(seedKey)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  position: 'relative',
                  height: 165,
                  borderRadius: 10,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: isHovered ? '2px solid #0CB4E4' : '2px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.05)',
                  transform: isHovered ? 'scale(1.03)' : 'scale(1)',
                  transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
                  boxShadow: isHovered ? '0 8px 28px rgba(253,186,89,0.3)' : '0 2px 10px rgba(0,0,0,0.5)',
                }}
              >
                {urlsLoading && !url ? (
                  <div style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,0.07)', animation: 'si-pulse 1.5s ease-in-out infinite' }} />
                ) : url ? (
                  <img src={url} alt={seed.name} draggable={false}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ImageIcon size={28} color="rgba(255,255,255,0.15)" />
                  </div>
                )}

                <div style={{ position: 'absolute', top: 7, left: 7, background: isHovered ? '#0CB4E4' : 'rgba(0,0,0,0.7)', color: isHovered ? '#000' : 'white', fontSize: 10, fontWeight: 700, borderRadius: 5, padding: '1px 6px', lineHeight: 1.6, transition: 'background 0.15s, color 0.15s' }}>
                  #{globalIndex + 1}
                </div>
                <div style={{ position: 'absolute', top: 7, right: 7, background: 'rgba(0,0,0,0.65)', color: 'rgba(255,255,255,0.6)', fontSize: 9, fontWeight: 600, fontFamily: 'monospace', borderRadius: 5, padding: '1px 6px', lineHeight: 1.6 }}>
                  cam {seed.cam}
                </div>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(253,186,89,0.10)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 10, opacity: isHovered ? 1 : 0, transition: 'opacity 0.15s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#0CB4E4', color: '#000', fontSize: 10, fontWeight: 700, borderRadius: 7, padding: '4px 12px' }}>
                    <Check size={11} strokeWidth={3} />
                    {t.sourceImages.selectThisView}
                  </div>
                </div>
                <div style={{ position: 'absolute', bottom: 7, left: 7, background: 'rgba(0,0,0,0.65)', color: 'rgba(255,255,255,0.5)', fontSize: 9, fontFamily: 'monospace', borderRadius: 4, padding: '1px 5px', lineHeight: 1.6 }}>
                  {seed.distanceMeters.toFixed(1)} m
                </div>
              </div>
            );
          })}
        </div>

        {/* Next — amber, always takes up space */}
        {hasMore ? (
          <button
            onClick={() => setPage(p => p + 1)}
            style={{
              flexShrink: 0, width: 64, height: 165,
              borderRadius: 10,
              background: '#0CB4E4',
              border: 'none',
              color: '#000',
              cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
              fontSize: 10, fontWeight: 700,
            }}
          >
            <ChevronRight size={20} />
            <span>{t.sourceImages.more}</span>
            <span style={{ fontSize: 9, opacity: 0.6 }}>{t.sourceImages.moreLeft(seeds.length - (page + 1) * SEEDS_PER_PAGE)}</span>
          </button>
        ) : (
          <div style={{ flexShrink: 0, width: 64 }} />
        )}

      </div>

      <style>{`
        @keyframes si-pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

// ─── Step 2: Expanded Carousel ────────────────────────────────────────────────

interface CarouselProps {
  results:     RankedImageCandidate[];
  imageUrls:   Record<string, string>;
  urlsLoading: boolean;
  activeIndex: number;
  selectedSeed: RankedImageCandidate | null;
  onSelect:    (i: number) => void;
  onBack:      () => void;
  onClose:     () => void;
}

const ImageCarousel: React.FC<CarouselProps> = ({
  results, imageUrls, urlsLoading, activeIndex, selectedSeed, onSelect, onBack, onClose,
}) => {
  const { t } = useI18n();
  const trackRef = useRef<HTMLDivElement>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const thumb = track.children[activeIndex] as HTMLElement | undefined;
    if (thumb) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeIndex]);

  const prev = () => onSelect(Math.max(0, activeIndex - 1));
  const next = () => onSelect(Math.min(results.length - 1, activeIndex + 1));

  // Keyboard: arrow keys navigate carousel, Escape closes lightbox
  const handleKey = useCallback((e: KeyboardEvent) => {
    const isArrow = e.key === 'ArrowLeft' || e.key === 'ArrowRight';
    if (isArrow) { e.preventDefault(); e.stopImmediatePropagation(); }

    if (lightbox !== null) {
      if (e.key === 'ArrowLeft')  { const n = Math.max(0, lightbox - 1); setLightbox(n); onSelect(n); }
      if (e.key === 'ArrowRight') { const n = Math.min(results.length - 1, lightbox + 1); setLightbox(n); onSelect(n); }
      if (e.key === 'Escape') setLightbox(null);
    } else {
      if (e.key === 'ArrowLeft')  onSelect(Math.max(0, activeIndex - 1));
      if (e.key === 'ArrowRight') onSelect(Math.min(results.length - 1, activeIndex + 1));
    }
  }, [lightbox, activeIndex, results.length, onSelect]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [handleKey]);

  if (results.length === 0) return null;

  const activeResult = results[activeIndex];

  return (
    <>
      <div
        className="fixed z-[1004] pointer-events-auto"
        style={{
          bottom: 0, left: 0, right: 0,
          background: 'rgba(0,0,0,0.78)',
          backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
          borderTop: '1px solid rgba(255,255,255,0.10)',
          padding: '10px 16px 12px',
        }}
      >
        {/* Top row: label + actions */}
        <div style={{ maxWidth: 1200, margin: '0 auto 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {results.length} images · cam {selectedSeed?.cam ?? '—'} around frame {selectedSeed?.frame ?? '—'}
            </span>
            {urlsLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>
                <Loader2 size={10} className="animate-spin" />
                <span>{t.sourceImages.loadingImages}</span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onBack}
              style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '4px 12px', cursor: 'pointer' }}>
              {t.sourceImages.backToViews}
            </button>
            <button onClick={onClose}
              style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '4px 12px', cursor: 'pointer' }}>
              {t.sourceImages.close}
            </button>
          </div>
        </div>

        {/* Thumbnail strip */}
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={prev} disabled={activeIndex === 0}
            style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: activeIndex === 0 ? 'rgba(255,255,255,0.2)' : 'white', cursor: activeIndex === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronLeft size={14} />
          </button>

          <div ref={trackRef} style={{ flex: 1, display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
            {results.map((img, i) => {
              const url = imageUrls[img.name];
              const isActive = i === activeIndex;
              return (
                <div
                  key={img.id}
                  onClick={() => { onSelect(i); setLightbox(i); }}
                  style={{
                    flexShrink: 0, position: 'relative', width: 96, height: 72,
                    borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                    border: isActive ? '2px solid #0CB4E4' : '2px solid rgba(255,255,255,0.08)',
                    background: 'rgba(255,255,255,0.05)',
                    transform: isActive ? 'scale(1.06)' : 'scale(1)',
                    transition: 'transform 0.15s, border-color 0.15s',
                  }}
                >
                  {urlsLoading && !url ? (
                    <div style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,0.06)', animation: 'si-pulse 1.5s ease-in-out infinite' }} />
                  ) : url ? (
                    <img src={url} alt={img.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageIcon size={20} color="rgba(255,255,255,0.2)" />
                    </div>
                  )}
                  {/* Rank badge */}
                  <div style={{ position: 'absolute', top: 4, left: 4, background: isActive ? '#0CB4E4' : 'rgba(0,0,0,0.65)', color: isActive ? '#000' : 'white', fontSize: 9, fontWeight: 700, borderRadius: 4, padding: '1px 5px', lineHeight: 1.6 }}>
                    #{i + 1}
                  </div>
                </div>
              );
            })}
          </div>

          <button onClick={next} disabled={activeIndex === results.length - 1}
            style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: activeIndex === results.length - 1 ? 'rgba(255,255,255,0.2)' : 'white', cursor: activeIndex === results.length - 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Metadata strip */}
        {activeResult && (
          <div style={{ maxWidth: 1200, margin: '8px auto 0', display: 'flex', gap: 16, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: '#0CB4E4', fontWeight: 600 }}>{activeResult.name}</span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>
              seq {activeResult.seq} · frame {activeResult.frame} · cam {activeResult.cam}
            </span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>
              {activeResult.distanceMeters.toFixed(1)} m
            </span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginLeft: 'auto' }}>
              {t.sourceImages.clickToZoom}
            </span>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <LightboxViewer
          img={results[lightbox]}
          url={imageUrls[results[lightbox]?.name]}
          index={lightbox}
          total={results.length}
          onClose={() => setLightbox(null)}
          onPrev={() => { const n = lightbox - 1; setLightbox(n); onSelect(n); }}
          onNext={() => { const n = lightbox + 1; setLightbox(n); onSelect(n); }}
        />
      )}

      <style>{`
        @keyframes si-pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const SourceImages: React.FC<SourceImagesProps> = ({
  viewerRef, manifest, sceneId, isActive, onClose, anchorRef,
}) => {
  const { t } = useI18n();
  const [step, setStep]         = useState<SourceImageStep>('idle');
  const stepRef                 = useRef<SourceImageStep>('idle');

  const [pickedPoint, setPickedPoint]           = useState<WorldPos | null>(null);
  const pickedPointRef                          = useRef<WorldPos | null>(null);
  const [pickedScreenPoint, setPickedScreenPoint] = useState<ScreenPos | null>(null);
  const [pickedSegment, setPickedSegment]       = useState<number | null>(null);

  const [loadingIndex, setLoadingIndex]   = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError]                 = useState<string | null>(null);

  // Two-stage results
  const [seedResults, setSeedResults]         = useState<RankedImageCandidate[]>([]);
  const [expandedResults, setExpandedResults] = useState<RankedImageCandidate[]>([]);
  const [selectedSeed, setSelectedSeed]       = useState<RankedImageCandidate | null>(null);
  const [resultsMode, setResultsMode]         = useState<'idle' | 'seed' | 'expanded'>('idle');

  // Image URLs
  const [imageUrls, setImageUrls]         = useState<Record<string, string>>({});
  const [urlsLoading, setUrlsLoading]     = useState(false);
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);

  const imageIndexStoreRef     = useRef<ImageIndexStore | null>(null);
  const pendingPointerIntentRef = useRef<PendingPointerIntent>(null);
  const rafRef                  = useRef<number>(0);

  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { pickedPointRef.current = pickedPoint; }, [pickedPoint]);

  const reset = useCallback(() => {
    pendingPointerIntentRef.current = null;
    setStep('idle');
    setPickedPoint(null);
    setPickedScreenPoint(null);
    setPickedSegment(null);
    setSeedResults([]);
    setExpandedResults([]);
    setSelectedSeed(null);
    setResultsMode('idle');
    setImageUrls({});
    setUrlsLoading(false);
    setActiveCarouselIndex(0);
    setSearchLoading(false);
    setLoadingIndex(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (!isActive) reset();
    else setStep('capturing');
  }, [isActive, reset]);

  // Fetch presigned URLs for seeds (step 1)
  useEffect(() => {
    // Also fires when returning from expanded mode (imageUrls was cleared by onBack,
    // so Object.keys(imageUrls).length === 0 acts as the re-fetch signal)
    if (resultsMode !== 'seed' || seedResults.length === 0) return;
    const names = seedResults.map(r => r.name).filter(Boolean);
    if (!names.length) return;

    let cancelled = false;
    setUrlsLoading(true);
    setImageUrls({});

    customerApi.getImagePresignedUrls(names)
      .then(res => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const e of res.data.urls) { if (e.url) map[e.imageName] = e.url; }
        setImageUrls(map);
      })
      .catch(err => { if (!cancelled) console.warn('[SourceImages] URL fetch failed:', err); })
      .finally(() => { if (!cancelled) setUrlsLoading(false); });

    return () => { cancelled = true; };
  // expandedResults in deps: when onBack clears it and flips mode back to 'seed',
  // this effect sees both resultsMode and expandedResults change → re-fires correctly
  }, [seedResults, resultsMode, expandedResults]);

  // Fetch presigned URLs for expanded results (step 2)
  useEffect(() => {
    if (resultsMode !== 'expanded' || expandedResults.length === 0) return;
    const names = expandedResults.slice(0, 50).map(r => r.name).filter(Boolean);
    if (!names.length) return;

    let cancelled = false;
    setUrlsLoading(true);
    setImageUrls({});
    setActiveCarouselIndex(0);

    customerApi.getImagePresignedUrls(names)
      .then(res => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const e of res.data.urls) { if (e.url) map[e.imageName] = e.url; }
        setImageUrls(map);
      })
      .catch(err => { if (!cancelled) console.warn('[SourceImages] URL fetch failed:', err); })
      .finally(() => { if (!cancelled) setUrlsLoading(false); });

    return () => { cancelled = true; };
  }, [expandedResults, resultsMode]);

  const ensureImageIndexLoaded = useCallback(async (): Promise<ImageIndexStore> => {
    if (imageIndexStoreRef.current) return imageIndexStoreRef.current;
    const indexUrl = getImageIndexUrlForScene(sceneId);
    if (!indexUrl) throw new Error(`No image index configured for scene: ${sceneId ?? 'unknown'}`);
    setLoadingIndex(true);
    try {
      const store = await fetchImageIndexStore(indexUrl);
      imageIndexStoreRef.current = store;
      return store;
    } finally { setLoadingIndex(false); }
  }, [sceneId]);

  const blockViewerNavigation = useCallback((viewer: any) => {
    if (!viewer) return;
    viewer.clickTransitioning = false;
    viewer.transitioningCameraTarget = false;
    if (viewer.nextCamera) viewer.nextCamera.copy(viewer.camera);
    if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);
  }, []);

  // Step 1: pick point → get 6 seed candidates
  const handleScenePick = useCallback(async (wp: WorldPos) => {
    try {
      const viewer = viewerRef.current;
      if (!viewer?.observerCoordinates) throw new Error('Viewer observer coordinates unavailable');
      if (!manifest) throw new Error('Manifest not loaded');

      setError(null);
      setSearchLoading(true);

      const geo = worldToGeodetic(
        wp,
        viewer.observerCoordinates as [number, number, number]
      );

      let seg = findSegmentForPOI(geo.latitude, geo.longitude, geo.altitude, manifest);

      if (seg === null) {
        seg = getFallbackSegmentFromViewer(viewer, wp);
        console.warn('[SourceImages] POI segment match failed, using viewer fallback segment:', seg);
      }

      if (seg === null) {
        throw new Error('Could not match point to a segment');
      }

      const store = await ensureImageIndexLoaded();
      const candidates = store.getCandidateImagesForSegment(seg, true);
      const seeds = selectSeedCandidates(wp, candidates, 30);

      setPickedPoint(wp);
      setPickedSegment(seg);
      setSeedResults(seeds);
      setExpandedResults([]);
      setSelectedSeed(null);
      setResultsMode('seed');
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to find source images');
      setStep('done');
    } finally {
      setSearchLoading(false);
    }
  }, [ensureImageIndexLoaded, manifest, viewerRef]);

  // Step 2: user picks a seed → expand to 50
  const handleSeedPick = useCallback(async (seed: RankedImageCandidate) => {
    try {
      if (!pickedPoint) throw new Error('No picked point');
      if (pickedSegment === null) throw new Error('No picked segment');

      setSearchLoading(true);
      setError(null);

      const store = await ensureImageIndexLoaded();
      // Reuse the already-computed segment — no need to re-project through geodetic
      const candidates = store.getCandidateImagesForSegment(pickedSegment, true);
      const sameCam    = candidates.filter(c => c.cam === seed.cam);
      const expanded   = expandFromSeedCandidate(pickedPoint, sameCam, seed, 50);

      setSelectedSeed(seed);
      setExpandedResults(expanded);
      setResultsMode('expanded');
      setActiveCarouselIndex(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to expand images');
    } finally {
      setSearchLoading(false);
    }
  }, [ensureImageIndexLoaded, pickedPoint, pickedSegment]);

  // RAF loop — keep marker synced
  useEffect(() => {
    const tick = () => {
      const viewer = viewerRef.current;
      const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
      const camera = viewer?.camera;
      const point  = pickedPointRef.current;
      if (canvas && camera && point) setPickedScreenPoint(projectToScreen(point, camera, canvas));
      else setPickedScreenPoint(null);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [viewerRef]);

  // Click handler
  useEffect(() => {
    const canvas = viewerRef.current?.renderer?.domElement as HTMLCanvasElement | undefined;
    if (!canvas || !isActive) return;

    const onPointerDown = () => {
      blockViewerNavigation(viewerRef.current);
      if (stepRef.current === 'capturing') pendingPointerIntentRef.current = { kind: 'create-point' };
      else pendingPointerIntentRef.current = null;
    };

    const onPointerUp = () => {
      const intent = pendingPointerIntentRef.current;
      pendingPointerIntentRef.current = null;
      const viewer = viewerRef.current;
      if (!viewer) return;
      blockViewerNavigation(viewer);
      if (intent?.kind !== 'create-point' || stepRef.current !== 'capturing') return;
      setTimeout(() => {
        if (!viewer?.clickPosition || !viewer?.observerCoordinates) return;
        blockViewerNavigation(viewer);
        const wp = viewer.clickPosition as WorldPos;
        void handleScenePick(wp);
      }, 0);
    };

    canvas.addEventListener('pointerdown', onPointerDown, true);
    canvas.addEventListener('pointerup',   onPointerUp,   true);
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown, true);
      canvas.removeEventListener('pointerup',   onPointerUp,   true);
    };
  }, [isActive, viewerRef, blockViewerNavigation, handleScenePick]);

  if (!isActive) return null;

  return (
    <>
      {step === 'capturing' && (
        <style>{`canvas { cursor: crosshair !important; }`}</style>
      )}

      {/* Instruction toast — only while waiting for click or loading */}
      {(step === 'capturing' || searchLoading || loadingIndex) && (
        <div className="fixed z-[1006] px-4 py-2 rounded-full pointer-events-none"
          style={{ top: 80, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>
          {searchLoading || loadingIndex
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Loader2 size={12} className="animate-spin" /> {t.sourceImages.finding}</span>
            : t.sourceImages.clickInstruction}
        </div>
      )}

      {/* Error toast */}
      {error && (
        <div className="fixed z-[1006] px-4 py-2 rounded-full"
          style={{ top: 80, left: '50%', transform: 'translateX(-50%)', background: 'rgba(220,38,38,0.8)', backdropFilter: 'blur(6px)', color: 'white', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{error}</span>
          <button onClick={() => { setError(null); setStep('capturing'); }} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Picked point marker */}
      {pickedScreenPoint?.visible && (
        <div className="fixed z-[1003] pointer-events-none"
          style={{ left: pickedScreenPoint.x - 14, top: pickedScreenPoint.y - 14 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: markerColor(0), border: '3px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 11, fontFamily: 'Arial' }}>
            {markerLabel(0)}
          </div>
        </div>
      )}

      {/* Close button (always visible once active) */}
      {resultsMode === 'idle' && (
        <button onClick={() => { reset(); onClose(); }}
          className="fixed z-[1006]"
          style={{ top: 68, right: 16, width: 32, height: 32, borderRadius: 10, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.15)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>
          <X size={14} />
        </button>
      )}

      {/* Step 1 — Seed tray */}
      {resultsMode === 'seed' && seedResults.length > 0 && (
        <SeedGrid
          key={seedResults.length}
          seeds={seedResults}
          imageUrls={imageUrls}
          urlsLoading={urlsLoading}
          onPick={handleSeedPick}
          onReset={() => {
            // Full reset clears all state — pickedPoint, segment, urls, expanded, seed, everything
            reset();
            // Then re-enter capturing so the user can click a new point
            setStep('capturing');
          }}
          onClose={() => { reset(); onClose(); }}
        />
      )}

      {/* Step 2 — Expanded carousel */}
      {resultsMode === 'expanded' && expandedResults.length > 0 && (
        <ImageCarousel
          results={expandedResults}
          imageUrls={imageUrls}
          urlsLoading={urlsLoading}
          activeIndex={activeCarouselIndex}
          selectedSeed={selectedSeed}
          onSelect={setActiveCarouselIndex}
          onBack={() => {
            // Fix 4: just flip mode — the seed useEffect already handles URL fetching
            setExpandedResults([]);
            setSelectedSeed(null);
            setActiveCarouselIndex(0);
            setResultsMode('seed');   // triggers the seed useEffect → re-fetches URLs cleanly
          }}
          onClose={() => { reset(); onClose(); }}
        />
      )}
    </>
  );
};

export default SourceImages;